/**
 * GLOW UP — SEO Enhancements
 * Schema.org, Breadcrumbs, Meta optimisation côté client
 */

// ── 1. BREADCRUMBS DYNAMIQUES ────────────────────────────────
class GlowUpBreadcrumbs {
  constructor() {
    this.init();
  }

  init() {
    this.injectStructuredData();
    this.enhanceBreadcrumbUI();
  }

  injectStructuredData() {
    const breadcrumb = document.querySelector('.breadcrumbs, nav[aria-label="breadcrumb"], .breadcrumb');
    if (!breadcrumb) return;

    const links = [...breadcrumb.querySelectorAll('a')];
    const items = links.map((link, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: link.textContent.trim(),
      item: link.href,
    }));

    // Ajouter la page courante
    const currentPage = breadcrumb.querySelector('[aria-current="page"], .breadcrumb__item--active');
    if (currentPage) {
      items.push({
        '@type': 'ListItem',
        position: items.length + 1,
        name: currentPage.textContent.trim(),
        item: window.location.href,
      });
    }

    if (items.length < 2) return;

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: items,
    });
    document.head.appendChild(script);
  }

  enhanceBreadcrumbUI() {
    const breadcrumb = document.querySelector('.breadcrumbs, nav[aria-label="breadcrumb"]');
    if (!breadcrumb) return;

    // Ajouter schema microdata
    breadcrumb.setAttribute('itemscope', '');
    breadcrumb.setAttribute('itemtype', 'https://schema.org/BreadcrumbList');

    breadcrumb.querySelectorAll('a').forEach((link, index) => {
      link.setAttribute('itemprop', 'url');
      const span = link.querySelector('span') || link;
      span.setAttribute('itemprop', 'name');

      const item = link.closest('li, .breadcrumb__item') || link;
      item.setAttribute('itemprop', 'itemListElement');
      item.setAttribute('itemscope', '');
      item.setAttribute('itemtype', 'https://schema.org/ListItem');

      const meta = document.createElement('meta');
      meta.setAttribute('itemprop', 'position');
      meta.setAttribute('content', String(index + 1));
      item.appendChild(meta);
    });
  }
}

// ── 2. SCHEMA.ORG PRODUIT ENRICHI ────────────────────────────
class GlowUpProductSchema {
  constructor() {
    this.init();
  }

  init() {
    // Enrichir les données structurées existantes des produits
    document.querySelectorAll('[itemtype="https://schema.org/Product"]').forEach((el) => {
      this.enrichProductSchema(el);
    });

    // Si sur page produit, ajouter les données manquantes
    if (window.location.pathname.includes('/products/')) {
      this.enhanceProductPage();
    }
  }

  enrichProductSchema(el) {
    // Ajouter brand si manquante
    if (!el.querySelector('[itemprop="brand"]')) {
      const shopName = document.querySelector('.logo, .header__heading')?.textContent?.trim();
      if (shopName) {
        const brand = document.createElement('span');
        brand.setAttribute('itemprop', 'brand');
        brand.setAttribute('itemscope', '');
        brand.setAttribute('itemtype', 'https://schema.org/Brand');
        brand.innerHTML = `<span itemprop="name">${shopName}</span>`;
        brand.style.display = 'none';
        el.appendChild(brand);
      }
    }
  }

  enhanceProductPage() {
    // Les données structured sont déjà injectées via Liquid dans product-information.liquid
    // Mais on peut enrichir avec des données client-side
    const existingSchema = document.querySelector('script[type="application/ld+json"]');
    if (!existingSchema) return;

    try {
      const data = JSON.parse(existingSchema.textContent);

      // Ajouter des propriétés manquantes si disponibles
      if (data['@type'] === 'Product') {
        // URL canonique
        if (!data.url) {
          data.url = window.location.href.split('?')[0];
        }

        // Mise à jour du script
        existingSchema.textContent = JSON.stringify(data);
      }
    } catch (e) {
      console.warn('[GlowUp SEO] Erreur parsing schema:', e);
    }
  }
}

// ── 3. META TAGS DYNAMIQUES ───────────────────────────────────
class GlowUpMeta {
  constructor() {
    this.init();
  }

  init() {
    this.addMissingMeta();
    this.optimizeOGImages();
  }

  addMissingMeta() {
    // Canonical link
    if (!document.querySelector('link[rel="canonical"]')) {
      const canonical = document.createElement('link');
      canonical.rel = 'canonical';
      canonical.href = window.location.href.split('?')[0].split('#')[0];
      document.head.appendChild(canonical);
    }

    // Robots meta
    if (!document.querySelector('meta[name="robots"]')) {
      const robots = document.createElement('meta');
      robots.name = 'robots';
      robots.content = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';
      document.head.appendChild(robots);
    }

    // Twitter card
    if (!document.querySelector('meta[name="twitter:card"]')) {
      const metas = [
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:site', content: window.location.hostname },
      ];
      metas.forEach(({ name, content }) => {
        const meta = document.createElement('meta');
        meta.name = name;
        meta.content = content;
        document.head.appendChild(meta);
      });
    }
  }

  optimizeOGImages() {
    const ogImage = document.querySelector('meta[property="og:image"]');
    if (!ogImage) return;

    // S'assurer que l'URL est absolue
    const url = ogImage.content;
    if (url && !url.startsWith('http')) {
      ogImage.content = window.location.origin + url;
    }

    // Ajouter dimensions si manquantes
    if (!document.querySelector('meta[property="og:image:width"]')) {
      const dims = [
        { property: 'og:image:width', content: '1200' },
        { property: 'og:image:height', content: '630' },
        { property: 'og:image:type', content: 'image/jpeg' },
      ];
      dims.forEach(({ property, content }) => {
        const meta = document.createElement('meta');
        meta.setAttribute('property', property);
        meta.content = content;
        document.head.appendChild(meta);
      });
    }
  }
}

// ── 4. ORGANISATION SCHEMA ────────────────────────────────────
function injectOrganizationSchema() {
  if (document.querySelector('script[data-schema="organization"]')) return;

  const shopName = document.querySelector('.logo-image, .header__heading')?.textContent?.trim()
    || document.title.split('–')[0]?.trim()
    || 'GLOW UP';

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-schema', 'organization');
  script.textContent = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: shopName,
    url: window.location.origin,
    logo: document.querySelector('link[rel="icon"]')?.href || '',
    sameAs: [], // Ajouter vos réseaux sociaux ici
  });
  document.head.appendChild(script);
}

// ── 5. WEBSITE SCHEMA ─────────────────────────────────────────
function injectWebsiteSchema() {
  if (document.querySelector('script[data-schema="website"]')) return;

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-schema', 'website');
  script.textContent = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    url: window.location.origin,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${window.location.origin}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  });
  document.head.appendChild(script);
}

// ── INIT SEO ──────────────────────────────────────────────────
document.addEventListener('DOMContentLoaded', () => {
  new GlowUpBreadcrumbs();
  new GlowUpProductSchema();
  new GlowUpMeta();
  injectOrganizationSchema();
  injectWebsiteSchema();

  console.log('[GlowUp SEO] ✅ Optimisations SEO chargées');
});
